<template lang="pug">
article.person-detail-card
  .person-visual
    .person-image-frame
      img.person-image(
        v-if='personImage'
        :src='personImage'
        :alt='formatFullName(person)'
        loading='eager'
        decoding='async'
        fetchpriority='high'
      )
      base-image-placeholder.person-image-placeholder(v-else)
    person-vcard.person-vcard(
      v-if='showContactDetails'
      :familyName='person.name.last'
      :givenName='person.name.first'
      :prefix='person.name.leadingTitle'
      :suffix='person.name.suffixTitle'
      :workTitle='person.category'
      :workRole='person.en.role'
      :email='person.email'
      :phone='person.phone'
      :addressStreet='person.address?.street'
      :addressLocality='person.address?.city'
      :addressPostalcode='person.address?.postcode'
      :website='person.website'
      :photo='personImage'
    )
  .person-main
    p.person-kicker {{ person.category }}
    .person-name-row
      base-koblenz-arrow.person-name-arrow
      h1.person-name {{ formatFullName(person) }}
    p.person-role {{ person.en.role }}
    .person-contact(v-if='showPrimaryMeta')
      p(v-if='showRoom')
        v-icon(size='18') mdi-home
        span Room {{ person.room }}
      p(v-if='showAddress')
        v-icon(size='18') mdi-map-marker
        a(:href='formattedAddress.link', target='_blank', rel='noopener noreferrer') {{ formattedAddress.label }}
      p(v-if='showPhone')
        v-icon(size='18') mdi-phone
        a(:href='telNr') {{ person.phone }}
      p(v-if='showEmail')
        v-icon(size='18') mdi-email
        a(:href='`mailto:${person.email}`') {{ person.email }}
    .person-links(v-if='showSecondaryMeta')
      section.person-panel(v-if='formattedExternalProfiles.length')
        h2 External Profiles
        .link-list
          a(
            v-for='externalProfile in formattedExternalProfiles'
            :key='externalProfile.title'
            :href='externalProfile.link'
            target='_blank'
            rel='noopener noreferrer'
          )
            span.external-profile-mark(v-if='externalProfile.mark', aria-hidden='true') {{ externalProfile.mark }}
            v-icon(v-else, size='18') {{ externalProfile.icon }}
            span {{ externalProfile.title }}
      section.person-panel(v-if='showWebsite')
        h2 Website
        a.website-link(:href='person.website', target='_blank', rel='noopener noreferrer')
          v-icon(size='18') mdi-web
          span {{ person.website }}
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { PersonInterface } from '~/interfaces/person'
import BaseKoblenzArrow from '~/components/base/KoblenzArrow.vue'
import { publicAssetPath } from '~/utils/assets'
import { formatExternalProfileLinks } from '~/utils/externalProfiles'
import { formatFullName } from '~/utils/person'

defineOptions({
  name: 'PersonDetailsCard',
})

const props = defineProps({
  person: {
    type: Object as () => PersonInterface,
    default: () => ({}),
  },
})

const isExternalMember = computed(() => !!props.person.external)

const personImage = computed(() => {
  if (!props.person.picture) return ''
  return publicAssetPath(props.person.picture)
})

const telNr = computed(() => {
  if (!props.person.phone) return ''
  return `tel:${props.person.phone.replace(/[^+0-9]/g, '')}`
})

const formattedAddress = computed(() => {
  if (!props.person.address) return null
  const { street, city, postcode } = props.person.address
  const label = `${street}, ${postcode} ${city}`

  return {
    label,
    link: `https://maps.google.com/?q=${encodeURIComponent(label)}`,
  }
})

const formattedExternalProfiles = computed(() => {
  if (!props.person?.externalProfiles) {
    return []
  }

  return formatExternalProfileLinks(props.person.externalProfiles)
})

const showContactDetails = computed(() => !isExternalMember.value)
const showRoom = computed(() => showContactDetails.value && !!props.person.room)
const showAddress = computed(
  () => showContactDetails.value && !!formattedAddress.value,
)
const showPhone = computed(
  () => showContactDetails.value && !!props.person.phone,
)
const showEmail = computed(
  () => showContactDetails.value && !!props.person.email,
)
const showWebsite = computed(() => !!props.person.website)
const showPrimaryMeta = computed(
  () =>
    showRoom.value || showAddress.value || showPhone.value || showEmail.value,
)
const showSecondaryMeta = computed(
  () => formattedExternalProfiles.value.length > 0 || showWebsite.value,
)
</script>

<style scoped>
.person-detail-card {
  display: grid;
  grid-template-columns: minmax(14rem, 18rem) minmax(0, 1fr);
  gap: 1.5rem;
  padding: 1.25rem;
  border: 1px solid var(--border-default);
  background: var(--surface-card);
}

.person-visual {
  display: grid;
  align-content: start;
  gap: 0.9rem;
}

.person-image-frame {
  overflow: hidden;
}

.person-image-placeholder {
  --image-placeholder-padding: 1.2rem;
  --image-placeholder-background: white;
  --image-placeholder-logo-width: 72%;
  --image-placeholder-logo-max-width: 8rem;
  --image-placeholder-logo-opacity: 0.32;
  --image-placeholder-logo-blur: 1.2px;
}

.person-image {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 4 / 5;
  object-fit: cover;
  object-position: center top;
}

.person-image-placeholder {
  aspect-ratio: 4 / 5;
}

.person-vcard {
  justify-self: start;
}

.person-main {
  min-width: 0;
}

.person-kicker {
  margin: 0 0 0.7rem;
  color: var(--color-accent-soft);
  text-transform: uppercase;
  letter-spacing: 0.16em;
  font-size: 0.76rem;
  font-weight: 700;
}

.person-name-row {
  display: flex;
  align-items: center;
  gap: 0.95rem;
}

.person-name-arrow {
  color: var(--brand-primary);
  font-size: clamp(1.35rem, 2.7vw, 2.15rem);
  margin-top: 0;
  flex: 0 0 auto;
}

.person-name {
  margin: 0;
  font-size: clamp(1.9rem, 3.2vw, 3rem);
  line-height: 0.95;
  letter-spacing: -0.04em;
}

.person-role {
  margin: 0.8rem 0 0;
  color: var(--color-text-muted);
  font-size: 1.05rem;
}

.person-contact {
  display: grid;
  gap: 0.7rem;
  margin-top: 1.4rem;
}

.person-contact p {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  margin: 0;
  color: var(--color-text);
}

.person-contact a {
  color: inherit;
  overflow-wrap: anywhere;
}

.person-links {
  display: grid;
  gap: 1rem;
  margin-top: 1.5rem;
}

.person-panel {
  padding: 1rem 1.1rem;
  border: 1px solid var(--border-default);
  background: var(--surface-soft);
}

.person-panel h2 {
  margin: 0 0 0.7rem;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.14em;
}

.link-list {
  display: grid;
  gap: 0.45rem;
}

.link-list a,
.website-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  width: fit-content;
  color: inherit;
}

.external-profile-mark {
  display: inline-grid;
  width: 1.15rem;
  height: 1.15rem;
  place-items: center;
  border: 1px solid currentColor;
  border-radius: 0.12rem;
  font-size: 0.55rem;
  font-weight: 800;
  line-height: 1;
}

@media (max-width: 960px) {
  .person-detail-card {
    grid-template-columns: 1fr;
  }

  .person-visual {
    justify-items: start;
  }

  .person-image-frame {
    width: min(100%, 16rem);
  }
}

@media (max-width: 760px) {
  .person-detail-card {
    padding: 1rem;
  }

  .person-name-row {
    gap: 0.7rem;
  }

  .person-name {
    font-size: 2rem;
  }
}
</style>
